/**
 * The trials search grammar, as the endpoint implements it: a quoted run is one exact phrase,
 * everything else splits on whitespace, and a `-` prefix is an exclusion.
 *
 * Shared by both frontends — the Knockout bundle compiles this subtree too (see
 * `backend/static/webpack.islands.js`), so highlighting cannot drift between the legacy list and
 * the React one while a tenant can be served either. Keep it dependency-free and JSX-free: the
 * legacy `tsc` compiles it under its own es5/amd config.
 */

/** The colour both lists mark a match with, from `backend/static/styles/trials.less`. */
export const SEARCH_HIGHLIGHT_COLOUR = '#FFDE03';

const EXACT_PHRASE_PATTERN = /"([^"]*)"/g;
const LEFTOVER_QUOTE_PATTERN = /"/g;
const TERM_SEPARATOR_PATTERN = /[\s,;]+/;
const EXCLUSION_PREFIX = '-';

/**
 * The terms a search text matches on. Anything the endpoint does not match on — exclusions,
 * quote characters, empty terms — is dropped, so a caller never marks up text the search did
 * not match. Longest first, so "maize trial" wins over "maize" where both apply.
 */
export function parseSearchTerms(searchText: string): string[] {
  const exactPhrases: string[] = [];
  const remainingText = (searchText || '')
    .replace(EXACT_PHRASE_PATTERN, (_match: string, phrase: string) => {
      exactPhrases.push(phrase);
      return ' ';
    })
    .replace(LEFTOVER_QUOTE_PATTERN, ' ');

  const words = remainingText
    .split(TERM_SEPARATOR_PATTERN)
    .filter((word) => word.length > 0 && !word.startsWith(EXCLUSION_PREFIX));

  return exactPhrases
    .concat(words)
    .map((term) => term.trim())
    .filter((term) => term.length > 0)
    .sort((first, second) => second.length - first.length);
}

/**
 * Splits text into alternating unmatched/matched pieces — one capture group, so the odd indexes
 * are the matches. Null when nothing would be marked, which callers use to skip the work.
 */
export function searchTermsRegExp(searchText: string): RegExp | null {
  const terms = parseSearchTerms(searchText);
  if (terms.length === 0) {
    return null;
  }
  const pattern = terms.map((term) => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
  return new RegExp(`(${pattern})`, 'gi');
}
